import React from 'react';
import { defaultMemoize } from 'reselect';

/**
 * !!!!!!!!!!!! This memoizes the services which can mess with your unit tests because the angular
 * !!!!!!!!!!!! services stick around from test to test (sometimes). If you're having issues with
 * !!!!!!!!!!!! your unit tests not working in a suite, try swapping this out for a simpler component.
 */
export interface AngularProviderHOCParams<OwnProps, PropsFromAngular, AngularServices> {

  /**
   * Fetch the necessary Angular services using the Service Locator.
   * This function will be called only once, when the component first renders, since references
   * to services never change.
   *
   * If the derived props are "service-like" singletons, they can be created here, instead of in
   * `selectPropsFromAngularServices`, to avoid having to memoize their creation.
   */
  getAngularServicesWithServiceLocator: () => AngularServices;

  /**
   * Similar to react-redux's mapStateToProps, but for props from Angular services. This gets called
   * on every update and passes the "props from Angular" down into the child component, so props
   * that stay the same across renders should be memoized where appropriate.
   *
   * @param {AngularServices} angularServices
   * @param {OwnProps} the props from the parent component
   * @returns {PropsFromAngular}
   */
  selectPropsFromAngularServices: (angularServices: AngularServices, props: OwnProps) => PropsFromAngular;
}

export type AngularProviderHOC<OwnProps, PropsFromAngular> =
    (wrappedComponent: React.ComponentType<OwnProps & PropsFromAngular>) => React.ComponentType<OwnProps>;


/**
 * This higher-order component wraps a child component in a provider that can inject props
 * derived from Angular services. This provider provides an easy way to keep a boundary between
 * the Angular world and the React world, and the props injected by this provider can be moved
 * into Redux connected components as the various Angular services are rewritten away from Angular.
 *
 * @deprecated This HOC makes it convenient to unit-test components, but it might be simpler
 *             to just grab the services in a component using ServiceLocator, and stub ServiceLocator.get
 *             in the component's tests to return mocked services. Over time, as those services are
 *             migrated away from Angular, those calls to ServiceLocator will be replaced with static
 *             references anyway.
 *
 * @param {AngularProviderHOCParams<OwnProps, PropsFromAngular, AngularServices>} params
 * @returns {AngularProviderHOC<OwnProps, PropsFromAngular>}
 */
export function angularProviderHOC<OwnProps, PropsFromAngular, AngularServices>(
  params: AngularProviderHOCParams<OwnProps, PropsFromAngular, AngularServices>
): AngularProviderHOC<OwnProps, PropsFromAngular> {

  const memoizedGetAngularServices = defaultMemoize(params.getAngularServicesWithServiceLocator);

  return wrappedComponent => {

    return function AngularProvider(props: OwnProps): JSX.Element {
      const angularServices = memoizedGetAngularServices();
      const propsFromAngular = params.selectPropsFromAngularServices(angularServices, props);

      const childProps: OwnProps & PropsFromAngular = {
        ...props,
        ...propsFromAngular,
      };
      return React.createElement(wrappedComponent, childProps);
    };

  };
}
